import {useCallback, useRef, useState} from "react";

/**
 * Hook that defers rendering/data-fetching until an element scrolls near the viewport.
 *
 * Uses IntersectionObserver with a generous rootMargin so the section starts
 * loading ~400px before the user actually sees it (no layout shift / jarring pop-in).
 *
 * Once visible, stays visible (no re-hiding on scroll-up).
 *
 * @param {{ rootMargin?: string }} options
 * @returns {{ ref: (node: HTMLElement | null) => void, isVisible: boolean }}
 *
 * @example
 * const { ref, isVisible } = useVisibilityLazy();
 * return (
 *   <div ref={ref}>
 *     {isVisible ? <ExpensiveComponent /> : <Skeleton />}
 *   </div>
 * );
 */
const useVisibilityLazy = ({rootMargin = "400px 0px"} = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const observerRef = useRef(null);

  const ref = useCallback(
    node => {
      // Disconnect previous observer if any
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      if (!node || isVisible) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        },
        {rootMargin}
      );

      observer.observe(node);
      observerRef.current = observer;
    },
    [isVisible, rootMargin]
  );

  return {ref, isVisible};
};

export default useVisibilityLazy;
